import { type ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Search, Undo2 } from 'lucide-react';

import type {
  ClientCertificateListItem,
  ClientListItem,
} from '@/types/api/queries';

import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/Input';
import { ListFilters } from '@/components/ListFilters';
import { TableActions } from '@/components/TableActions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { useDebounce, useList, useTableColumns } from '@/hooks';

import { CLIENT_CERTIFICATES_LIST_QUERY_KEY } from '@/queries';

import { centsToMoneyFormat } from '@/helpers';

import { CERTIFICATE_STATUS_LABEL_KEYS } from '@/constants';
import { DISPLAY_DATES_FORMAT } from '@/constants/dates';

import { CertificateComposition } from './CertificateComposition';
import { RefundCertificateDialog } from './RefundCertificateDialog';

type RefundConfig = {
  cashRegistersUrl: string;
  refundReasonsUrl: string;
};

type ClientCertificatesTableProps = {
  fetchUrl: string;
  byCodeUrl: string;
  clientsSearchUrl?: string;
  /** Base path of the clients section — the Buyer column links to `${clientsUrl}/${id}` */
  clientsUrl?: string;
  buyerClientId?: number;
  pageSize?: number;
  refund?: RefundConfig;
  onRefundSuccess?: () => void;
};

export const ClientCertificatesTable = (
  props: ClientCertificatesTableProps,
) => {
  const {
    fetchUrl,
    byCodeUrl,
    clientsSearchUrl,
    clientsUrl,
    buyerClientId,
    pageSize = 50,
    refund,
    onRefundSuccess: propOnRefundSuccess,
  } = props;
  const { t } = useTranslation();

  const [refundTarget, setRefundTarget] =
    useState<ClientCertificateListItem | null>(null);

  const getClientLabel = useCallback((option: unknown) => {
    const client = option as ClientListItem;
    return `${client.first_name} ${client.last_name}`;
  }, []);

  const getClientValue = useCallback((option: unknown) => {
    const client = option as ClientListItem;
    return String(client.id);
  }, []);

  const transformClientsResponse = useCallback(
    (response: unknown) => (response as { data: ClientListItem[] }).data,
    [],
  );

  // Buyer-scoped mode pins the list to one buyer, so the client filter is neither
  // rendered nor honoured — the two modes are mutually exclusive.
  const clientFilterUrl = buyerClientId ? undefined : clientsSearchUrl;

  const transformFilters = useCallback(
    (applied: Record<string, unknown>) => {
      const { clients, from, till } = applied;
      const filters: Record<string, unknown> = {};

      if (from || till) {
        filters.period = { from, till };
      }
      if (buyerClientId) {
        return { ...filters, clients: [buyerClientId] };
      }
      if (!clients) {
        return filters;
      }

      const client = clients as ClientListItem;

      return { ...filters, clients: [client.id] };
    },
    [buyerClientId],
  );

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useDebounce('', 500);

  const { data, isLoading, table, filters, refetch } =
    useList<ClientCertificateListItem>({
      fetch: { url: fetchUrl, queryKey: CLIENT_CERTIFICATES_LIST_QUERY_KEY },
      pagination: { pageSize },
      sorting: { id: 'purchased_at', desc: true },
      search: debouncedSearchValue,
      filters: {
        // Required, not cosmetic: useList only sends `filters` when something is
        // applied, so without this default the buyer scope silently falls away
        // and the table lists the whole company.
        ...(buyerClientId
          ? { defaults: { clients: { id: buyerClientId } } }
          : {}),
        transform: transformFilters,
        definitions: [
          ...(clientFilterUrl
            ? [
                {
                  type: 'autocomplete' as const,
                  name: 'clients',
                  label: t('Client'),
                  options: [],
                  fetchUrl: clientFilterUrl,
                  queryKey: ['clients_filter', 'client_certificates'],
                  transformResponse: transformClientsResponse,
                  getOptionLabel: getClientLabel,
                  getOptionValue: getClientValue,
                },
              ]
            : []),
          { type: 'date', name: 'from', label: t('Sold from') },
          { type: 'date', name: 'till', label: t('Sold till') },
        ],
      },
    });

  const onRefundSuccess = useCallback(() => {
    refetch();
    propOnRefundSuccess?.();
  }, [refetch, propOnRefundSuccess]);

  const columns = useTableColumns<ClientCertificateListItem>(
    ({ sortable, display }) => {
      const buyerColumns = buyerClientId
        ? []
        : [
            display('client', t('Buyer'), (row) =>
              clientsUrl ? (
                <a
                  href={`${clientsUrl}/${row.client.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium hover:underline"
                >
                  {row.client.name}
                </a>
              ) : (
                row.client.name
              ),
            ),
          ];

      const baseColumns = [
        display('code', t('Code'), (row) => (
          <span className="font-mono font-medium tracking-wider">
            {row.code}
          </span>
        )),
        ...buyerColumns,
        display('status', t('Status'), (row) => (
          <Badge variant={row.status === 'active' ? 'default' : 'secondary'}>
            {t(CERTIFICATE_STATUS_LABEL_KEYS[row.status])}
          </Badge>
        )),
        display('sale_location', t('Sale location'), (row) => (
          <span>{row.sale_location.name}</span>
        )),
        sortable('purchased_at', t('Sold at'), { format: 'date' }),
        display('expires_at', t('Valid until'), (row) =>
          row.expires_at
            ? format(parseISO(row.expires_at), DISPLAY_DATES_FORMAT)
            : t('No expiry'),
        ),
        display('price_cents', t('Face value'), (row) =>
          centsToMoneyFormat(row.price_cents),
        ),
        display('liability_balance_cents', t('Remainder'), (row) => (
          <span className="font-semibold">
            {centsToMoneyFormat(row.liability_balance_cents)}
          </span>
        )),
      ];

      if (!refund) {
        return baseColumns;
      }

      return [
        ...baseColumns,
        display('actions', t('Actions'), (row) => (
          <TableActions>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="size-8 cursor-pointer p-0"
                  disabled={!row.refundable}
                  onClick={() => setRefundTarget(row)}
                >
                  <Undo2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('Refund the remainder')}</TooltipContent>
            </Tooltip>
          </TableActions>
        )),
      ];
    },
  );

  const renderSubRow = useCallback(
    (row: ClientCertificateListItem) => (
      <CertificateComposition
        certificate={row}
        byCodeUrl={byCodeUrl}
      />
    ),
    [byCodeUrl],
  );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  return (
    <>
      <div className="mb-4 space-y-4">
        <div className="w-full md:w-1/3">
          <Input
            label={t('Search by code')}
            value={searchValue}
            onChange={onSearchChange}
            icon={<Search className="size-4" />}
          />
        </div>

        <ListFilters {...filters} />
      </div>

      <DataTable
        data={data}
        columns={columns}
        pending={isLoading}
        renderSubRow={renderSubRow}
        {...table}
      />

      {!!refund && (
        <RefundCertificateDialog
          isOpen={refundTarget !== null}
          certificate={refundTarget}
          cashRegistersUrl={refund.cashRegistersUrl}
          refundReasonsUrl={refund.refundReasonsUrl}
          onSuccess={onRefundSuccess}
          onClose={() => setRefundTarget(null)}
        />
      )}
    </>
  );
};
